import {writable, readable} from "svelte/store";
import {
    default_store_section, get_config,
    get_store_packs_route, get_player_state_route, get_static_data_route,
} from "./constants";
import {goTo} from "./utils.js";
import { tagNetworkFailure, tagBodyReadFailure } from "./lib/network-failure.js";
import { isSessionExpired } from "./lib/session-expiry.js";
import { hasPaymentHubPrice, partitionByPrice } from "./lib/pack-pricing.js";

function getCookie(cookieName) {
    const cookieArr = document.cookie.split(";");
    for (let i = 0; i < cookieArr.length; i++) {
        const cookiePair = cookieArr[i].split("=");
        if (cookieName === cookiePair[0].trim()) {
            return decodeURIComponent(cookiePair[1]);
        }
    }
    return null;
}

function updateUserName(newName) {
    const b = document.querySelector('.user-info b');
    if (!b) return;

    // Find the first non-empty text node in <b>
    let textNode = Array.from(b.childNodes)
        .find(n => n.nodeType === Node.TEXT_NODE && n.nodeValue.trim() !== '');

    if (textNode) {
        // Preserve any existing leading/trailing spaces
        const leading = textNode.nodeValue.match(/^\s*/)?.[0] ?? '';
        const trailing = textNode.nodeValue.match(/\s*$/)?.[0] ?? '';
        textNode.nodeValue = `${leading}${newName}${trailing}`;
    } else {
        // If no text node exists (only SVG), append the name
        b.append(newName);
    }
}

function safeJsonParse(value, fallback = null) {
    if (value == null || value === 'undefined') return fallback;
    try {
        return JSON.parse(value);
    } catch {
        return fallback;
    }
}

export const user = writable(
    safeJsonParse(localStorage.getItem("user"))
);

if (getCookie("connect.sid") == null) {
    user.set(null);
    localStorage.removeItem("user");
}

export const storePacks = writable([]);
export const subscriptions = writable([]);
export const storePacksError = writable(false);
export const storePacksLoading = writable(false);
export const gameConfig = writable({});
export const systemMessages = writable({});
// Mobile: whether the burger-collapsed user area is expanded. Shared so the tab
// bar can re-center the active tab clear of the expanded controls.
export const userMenuOpen = writable(false);
export const rechargeRewardsEvent = writable([]);
export const adventCalendarEvent = writable([]);
export const luckyDrawEvent = writable([]);
export const battlePassEvent = writable({});
export const chainPromoEvent = writable({});
export const selectedCurrency = writable(
    localStorage.currency ? localStorage.getItem("currency") : null
);
export const storeSection = writable(
    localStorage.storeSection
        ? localStorage.getItem("storeSection")
        : default_store_section
);
export const isRedirected = writable(false);
export const scrollToPackId = writable(null);

// Single source of truth for the mobile/desktop split (matches the 768px SCSS breakpoint).
const MOBILE_MEDIA_QUERY = "(max-width: 768px)";
export const isMobile = readable(
    typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? window.matchMedia(MOBILE_MEDIA_QUERY).matches
        : false,
    (set) => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
        const mql = window.matchMedia(MOBILE_MEDIA_QUERY);
        const update = (e) => set(e.matches);
        set(mql.matches);
        mql.addEventListener("change", update);
        return () => mql.removeEventListener("change", update);
    }
);

export function navigateToSection(section) {
    storeSection.set(section);
    goTo('/supplies/' + section, true);
}
export const currentAdventClaimed = writable(false);

let cachedStaticData = null;
let pendingGetStorePacks = null;

// web-shop's JSON 403 means the session is gone — expired, or revoked server-side
// (isSessionExpired; a 403 from Cloudflare in front of it does not). Clearing the user store
// swaps the whole Shop out for <Login/> in App.svelte, which is what stops a signed-out player
// sitting on a dead page. Exported so every caller that talks to the API directly — not just
// fetchJson — can react the same way (Rollbar #2519).
export { isSessionExpired };
export const clearExpiredSession = () => {
    user.set(null);
    localStorage.removeItem("user");
};

// For a player action the guard refused (a buy, a collect, a claim): the session ended mid-visit,
// which is worth seeing (#1349's ended 8s after an authenticated get-packs), so log it at info and
// clear it. A 403 while the shop loads is a returning player's expired cookie: clearExpiredSession
// alone, silently.
export const endExpiredSession = (route) => {
    Rollbar.info('Session expired during ' + route);
    clearExpiredSession();
};

function fetchJson(url, options = {}) {
    return fetch(url, {
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        ...options,
    }).catch(tagNetworkFailure).then((res) => {
        if (isSessionExpired(res)) {
            clearExpiredSession();
            return null;
        }
        if (!res.ok) throw new Error(`${url} failed: ${res.status}`);
        return res.json().catch(tagBodyReadFailure);
    });
}

export const getStorePacks = (force, { fetchPlayerState = true, fetchStaticData = true } = {}) => {
    if (pendingGetStorePacks && !force) return pendingGetStorePacks;

    storePacksError.set(false);
    storePacksLoading.set(true);

    const needStaticData = fetchStaticData || !cachedStaticData;

    const safeJson = (promise) => promise.catch((err) => {
        // fetchJson tags a dropped connection at the boundary; that is the bulk of this
        // item (#2363 / #2392: "Load failed" / "Failed to fetch") and is not an app fault.
        // A backend status (#2385: static-data 502) or a handler bug stays at warning.
        if (err?.isNetworkFailure) {
            Rollbar.info("Non-fatal fetch network failure: " + err);
        } else {
            Rollbar.warning("Non-fatal fetch error: " + err);
        }
        return false;
    });

    pendingGetStorePacks = Promise.all([
        fetchJson(get_store_packs_route, {
            method: "POST",
            body: JSON.stringify(force ? { force: true } : {}),
        }),
        fetchPlayerState
            ? safeJson(fetchJson(get_player_state_route, { method: "POST" }))
            : Promise.resolve(false),
        needStaticData
            ? safeJson(fetchJson(get_static_data_route))
            : Promise.resolve(cachedStaticData),
    ])
        .then(([packsData, playerState, staticData]) => {
            // get-packs is essential — auth failure or missing = fatal
            if (!packsData) {
                storePacksLoading.set(false);
                return;
            }

            if (packsData.statusCode === 500) {
                Rollbar.error("Get packs status code 500 " + packsData.message);
                storePacksError.set(true);
                storePacksLoading.set(false);
                return;
            }

            // Normalize failed non-fatal endpoints to false
            if (playerState && playerState.statusCode === 500) playerState = false;
            if (staticData && staticData.statusCode === 500) staticData = false;

            // Cache static data on successful fetch
            if (needStaticData && staticData && staticData !== false) {
                cachedStaticData = staticData;
            }

            const sd = staticData && staticData !== false ? staticData : cachedStaticData;

            // Player name update (from player-state)
            if (playerState) {
                const userData = safeJsonParse(localStorage.getItem('user'), { user: {} });
                const newName = playerState.name;
                const oldName = userData.user?.username || '';

                if (newName && newName !== oldName) {
                    setTimeout(() => {
                        updateUserName(newName);
                        userData.user.username = newName;
                        localStorage.setItem('user', JSON.stringify(userData));
                    }, 100);
                }
            }

            // Static data → gameConfig
            if (sd) {
                gameConfig.set({
                    gameItems: sd.gameItems,
                    commanders: sd.commanders,
                    heroEquipment: sd.heroEquipment || {},
                    temporaryItems: sd.temporaryItems || {},
                });

                bonuses.set(sd.bonuses);

                if (sd.growthFund !== undefined) growthFundConfig.set(sd.growthFund);
                if (sd.monsterGrowthFund !== undefined) monsterGrowthFundConfig.set(sd.monsterGrowthFund);
                if (sd.resourceTroveBonuses !== undefined) resourceTroveBonuses.set(sd.resourceTroveBonuses);
            }

            // Packs data. A paid pack PaymentHub could not price is left out rather than
            // rendered: it cannot be bought, and formatting its placeholder currency throws,
            // which took the whole shop down with it (see pack-pricing.js).
            const { sellable: packs, hiddenIds } = partitionByPrice(packsData.packs);
            if (hiddenIds.length > 0) {
                Rollbar.warning("Packs hidden without a PaymentHub price", { packIds: hiddenIds });
            }
            storePacks.set(packs);

            if (packs.length === 0 && (localStorage.storeSection === 'promos' || (localStorage.storeSection !== 'freeGifts' && localStorage.storeSection !== 'adventCalendar'))) {
                if (packsData.subscriptions.length > 0) {
                    storeSection.set("freeGifts");
                    goTo('/supplies/freeGifts', true);
                } else {
                    if (packsData.adventCalendar !== undefined) {
                        storeSection.set("adventCalendar");
                        goTo('/supplies/adventCalendar', true);
                    }
                }
            }

            if (packsData.subscriptions !== undefined) {
                subscriptions.set(packsData.subscriptions);
            }

            if (packsData.rechargeRewardsEvent !== undefined) {
                rechargeRewardsEvent.set(packsData.rechargeRewardsEvent);
            }

            if (packsData.seasonPassEvent !== undefined) {
                battlePassEvent.set(packsData.seasonPassEvent);
            }

            if (packsData.adventCalendar !== undefined) {
                adventCalendarEvent.set(packsData.adventCalendar);
            }

            if (packsData.luckyDrawEvent !== undefined) {
                luckyDrawEvent.set(packsData.luckyDrawEvent);
            }

            // Reset when absent so the Chain Deals tab disappears once the event ends
            chainPromoEvent.set(packsData.chainPromoEvent ?? {});

            maxWebshopDiscount.set(packsData.webshopDiscount);

            if (packsData.systemMessages !== undefined) {
                systemMessages.set(packsData.systemMessages);
            }

            // Player state
            if (playerState) {
                castleLevel.set(playerState.castleLevel);
                vipLevel.set(playerState.vipLevel);

                if (playerState.playerGrowthFund !== undefined) playerGrowthFund.set(!!playerState.playerGrowthFund);
                if (playerState.playerMonsterGrowthFund !== undefined) playerMonsterGrowthFund.set(!!playerState.playerMonsterGrowthFund);
                if (playerState.claimedGrowthFundPrizes) claimedGrowthFundPrizes.set(playerState.claimedGrowthFundPrizes);
                if (playerState.claimedMonsterGrowthFundPrizes) claimedMonsterGrowthFundPrizes.set(playerState.claimedMonsterGrowthFundPrizes);
                if (playerState.defeatedMonsterLevels) defeatedMonsterLevels.set(playerState.defeatedMonsterLevels);
                playerStateLoaded.set(true);
            }

            currentAdventClaimed.update(value => false);

            // Set currency from the first priced pack, or from subscriptions, or keep existing
            const pricedPack = packs.find(hasPaymentHubPrice);
            if (pricedPack) {
                selectedCurrency.set(pricedPack.paymentHub.price.currency);
            } else if (packsData.subscriptions?.length > 0 && packsData.subscriptions[0]?.paymentHub?.price?.currency) {
                selectedCurrency.set(packsData.subscriptions[0].paymentHub.price.currency);
            }

            storePacksLoading.set(false);
        })
        .catch((error) => {
            // Only a failure tagged at the fetch boundary is a dropped connection (#383 / #380).
            // Anything else — a backend status, or a TypeError thrown by the .then() handler
            // above — is a real fault and must stay at error level.
            if (error?.isNetworkFailure) {
                Rollbar.info("Get packs network failure: " + error);
            } else {
                Rollbar.error("Get packs error " + error);
            }
            storePacksError.set(true);
            storePacksLoading.set(false);
        })
        .finally(() => { pendingGetStorePacks = null; });

    return pendingGetStorePacks;
};
export const giftFriend = writable({
    startPurchase: false,
    selectedPack: {},
    selectedPackDetails: {},
    message: '',
});
let pendingGetConfig = null;
export const getConfig = () => {
    if (pendingGetConfig) return pendingGetConfig;

    pendingGetConfig = fetch(get_config, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
        },
    })
        .catch(tagNetworkFailure)
        .then((response) => {
            if (isSessionExpired(response)) {
                clearExpiredSession();
                return;
            }
            if (!response.ok) throw new Error(`Get config failed: ${response.status}`);
            return response.json().catch(tagBodyReadFailure);
        })
        .then((data) => {
            if (data) config.set(data);
        })
        .catch((error) => {
            // Same split as getStorePacks, and for the same reason: only a boundary-tagged
            // rejection is a dropped connection (#1442). Backend statuses and handler bugs stay
            // at error level (#2280 / #2300).
            if (error?.isNetworkFailure) {
                Rollbar.info("Get config network failure: " + error);
            } else {
                Rollbar.error("Get config error " + error);
            }
        })
        .finally(() => { pendingGetConfig = null; });

    return pendingGetConfig;
};

export const castleLevel = writable(0);
export const vipLevel = writable(0);
export const maxWebshopDiscount = writable(0);
export const bonuses = writable({});
export const config = writable({});

// Advancement Rewards stores
export const growthFundConfig = writable(null);
export const monsterGrowthFundConfig = writable(null);
export const resourceTroveBonuses = writable({});
// True once a /api/store/player-state payload has been applied. The fetch is non-fatal,
// so until then every ownership flag below is merely its default and must not be
// read as "the player does not own it".
export const playerStateLoaded = writable(false);
export const playerGrowthFund = writable(false);
export const playerMonsterGrowthFund = writable(false);
export const claimedGrowthFundPrizes = writable([]);
export const claimedMonsterGrowthFundPrizes = writable([]);
export const defeatedMonsterLevels = writable([]);
